import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  RadioGroup,
  Radio,
  FormControlLabel,
  Divider,
  Chip,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  IconButton,
  Stack,
  Checkbox,
  ListItemText,
  InputAdornment,
  Autocomplete,
  Grid,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import Header from "../components/Admin/header/Header";
import Sidebar from "../components/Admin/Sidebar/Sidebar";
import { Search, UploadIcon, UserPlus, X, ArrowLeft } from "lucide-react";
import AddIcon from "@mui/icons-material/Add";
import { axiosCall } from "../utils/axiosCall";
import constants from "../config/constants";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { getStateApi } from "../services/state.service";
import { useGetApi } from "../hooks/useGetApi";
import { getCityApi } from "../services/city.service";
import { getRelationshipApi } from "../services/relationship.service";
import PatientSchema  from "../joi/patient/patient.schema";
import { toast } from "react-toastify";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";


const calcAgeFromDOB = (value) => {
  const dobDate = new Date(value);
  const today = new Date();
  let age = today.getFullYear() - dobDate.getFullYear();
  // Adjust age if birthday hasn't occurred this year
  const monthDiff = today.getMonth() - dobDate.getMonth();
  const dayDiff = today.getDate() - dobDate.getDate();
  if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
    age--;
  }
  return age;
};

function PatientForm() {
  const user = useSelector((state) => state.user);
  const navigate = useNavigate();
  const { patientId } = useParams();
  const [isEditMode, setIsEditMode] = useState(false);
  const [errors, setErrors] = useState([]);
  const [patientInfo, setPatientInfo] = React.useState({
    UHID: "",
    title: "",
    firstName: "",
    lastName: "",
    phone: "",
    dob: "",
    age: "",
    gender: "",
    address: "",
    city: "",
    state: "",
    pinCode: "",
    registrationDate: new Date(),

    emergencyContactName: "",
    emergencyContactRelationship: "",
    emergencyContactNumber: "",

    insuranceProvider: "",
    policyNumber: "",
    coverage: "",

    patientImage: "",
  });
  const { dataList: stateList } = useGetApi({
    apiFunction: getStateApi,
    params: { limit: Number.MAX_SAFE_INTEGER },
    dataKey: "states",
  });
  const { dataList: cityList } = useGetApi({
    apiFunction: getCityApi,
    params: { limit: Number.MAX_SAFE_INTEGER },
    dataKey: "cities",
  });
  const { dataList: relationshipList } = useGetApi({
    apiFunction: getRelationshipApi,
    params: { limit: Number.MAX_SAFE_INTEGER },
    dataKey: "relationships",
  });

  const getUHId = async () => {
    try {
      const getUHID = await axiosCall(
        constants.GET_METHOD,
        constants.GET_UHID,
        null,
        null,
        user.token
      );

      if (getUHID.isSuccess) {
        // Extract UHID from response
        // Backend returns: { status: 200, data: "KSG-0018038" }
        // Controller wraps it: { isSuccess: true, message: "success", code: 200, data: "KSG-0018038" }
        // axiosCall wraps it: { isSuccess: true, data: { isSuccess: true, message: "success", code: 200, data: "KSG-0018038" } }
        // So getUHID.data.data should be the UHID string
        
        let uhidString = "";
        
        // Try getUHID.data.data first (expected structure)
        if (getUHID.data?.data) {
          if (typeof getUHID.data.data === 'string') {
            uhidString = getUHID.data.data;
          } else if (typeof getUHID.data.data === 'object' && getUHID.data.data !== null) {
            // If it's an object, try to extract the value
            uhidString = getUHID.data.data.UHID || getUHID.data.data.value || getUHID.data.data.data || "";
          } else {
            uhidString = String(getUHID.data.data);
          }
        }
        // Fallback: try getUHID.data if it's a string
        else if (getUHID.data && typeof getUHID.data === 'string') {
          uhidString = getUHID.data;
        }
        
        // Only set if we have a valid string (not "[object Object]")
        if (uhidString && uhidString !== "[object Object]") {
          setPatientInfo({
            ...patientInfo,
            UHID: uhidString,
          });
        } else {
          // If we can't extract a valid UHID, set empty string
          setPatientInfo({
            ...patientInfo,
            UHID: "",
          });
        }
      }
    } catch (error) {
      console.log(error);
    }
  };

  const fetchPatient = async () => {
    try {
      const getPatient = await axiosCall(
        constants.GET_METHOD,
        constants.GET_PATIENT + `/${patientId}`,
        null,
        null,
        user.token
      );

      if (getPatient.isSuccess) {
        console.log(getPatient.data.data);
        const patientData = getPatient.data.data;
        // Extract UHID and ensure it's a string - handle all possible types
        let uhidString = "";
        if (patientData.UHID) {
          if (typeof patientData.UHID === 'string') {
            uhidString = patientData.UHID;
          } else if (typeof patientData.UHID === 'number') {
            uhidString = patientData.UHID.toString();
          } else if (typeof patientData.UHID === 'object' && patientData.UHID !== null) {
            // If UHID is an object, try to extract the actual value
            // Check common property names that might contain the UHID value
            if (patientData.UHID.value !== undefined) {
              uhidString = String(patientData.UHID.value);
            } else if (patientData.UHID.UHID !== undefined) {
              uhidString = String(patientData.UHID.UHID);
            } else if (patientData.UHID.toString && typeof patientData.UHID.toString === 'function') {
              const toStringResult = patientData.UHID.toString();
              // Only use toString if it doesn't return "[object Object]"
              uhidString = toStringResult !== '[object Object]' ? toStringResult : '';
            } else {
              uhidString = '';
            }
          } else {
            uhidString = String(patientData.UHID);
          }
        }
        
        setPatientInfo({
          ...patientData,
          UHID: uhidString,
          city: patientData.city?._id || patientData.city,
          state: patientData.state?._id || patientData.state,
          age: calcAgeFromDOB(patientData.dob),
        });
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    if (!patientId) {
      getUHId();
    }
  }, []);

  useEffect(() => {
    if (patientId) {
      setIsEditMode(true);
      fetchPatient(patientId);
    }
  }, [patientId]);

  const formData = new FormData();

  const handleUpload = async (e) => {
    formData.append("type", "patient");
    formData.append("image", e.target.files[0]);

    const uploadPic = await axiosCall(
      constants.POST_METHOD,
      constants.UPLOAD_IMAGE,
      formData,
      null,
      user.token
    );

    if (uploadPic.isSuccess) {
      setPatientInfo({
        ...patientInfo,
        patientImage: uploadPic.data?.data?.Location,
      });
    } else {
      alert(uploadPic.error?.message);
    }
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (isEditMode) {
      updatePatient();
    } else {
      await registerPatient();
    }
  };

  const registerPatient = async () => {
    
    // Log the current patient info for debugging
    console.log("🔍 DEBUGGING Patient Registration");
    console.log("📋 Current patientInfo:", patientInfo);
    
    // Prepare data for validation (adding contactNo field for Joi validation)
    const validationData = {...patientInfo, contactNo: patientInfo?.phone};
    console.log("✅ Data being validated:", validationData);
    
    const { error } = PatientSchema.validate(validationData, {
      abortEarly: false,
    });
    
    if(error) {
      console.log("❌ VALIDATION FAILED!");
      console.log("🚨 Validation errors:", error.details);
      
      const errors = []
      const errorMessages = [];
      error.details.forEach((err) => {
        errors.push(err?.context?.key);
        errorMessages.push(err.message);
        console.log(`❌ Field '${err?.context?.key}': ${err.message}`);
      });
      
      setErrors(errors);
      
      console.log("📝 Missing/Invalid fields summary:", errorMessages);
      
      // Show the actual validation error messages, not just field names
      if (errorMessages.length === 1) {
        toast.error(errorMessages[0]);
      } else {
        toast.error(
          `Please fix the following issues:\n• ${errorMessages.join('\n• ')}`
        );
      }
      return;
    }
    
    console.log("✅ Frontend validation passed!");
    
    try {
      // Log what we're sending to backend
      console.log("📤 Sending to backend:", patientInfo);
      
      const registerPatient = await axiosCall(
        constants.POST_METHOD,
        constants.REGISTER_PATIENT,
        patientInfo,
        null,
        user.token
      );

      console.log("📨 Backend response:", registerPatient);

      if (registerPatient.isSuccess) {
        console.log("🎉 Patient registered successfully!");
        toast.success("Patient registered successfully!");
        
        // reset patient info
        setPatientInfo({
          UHID: "",
          title: "",
          firstName: "",
          lastName: "",
          phone: "",
          dob: "",
          age: "",
          gender: "",
          address: "",
          city: "",
          state: "",
          pinCode: "",
          registrationDate: new Date(),

          emergencyContactName: "",
          emergencyContactRelationship: "",
          emergencyContactNumber: "",

          insuranceProvider: "",
          policyNumber: "",
          coverage: "",

          patientImage: "",
        });
        navigate("/patient");
      } else {
        console.log("❌ Backend registration failed!");
        console.log("🚨 Backend error details:", registerPatient);
        toast.error(registerPatient.error?.message || "Registration failed. Please check all required fields.");
      }
    } catch (error) {
      console.log("💥 Exception during registration:", error);
      toast.error("An error occurred during registration. Please try again.");
    }
  };

  const updatePatient = async (e) => {
    if (e) e.preventDefault();
    try {
      const updatePatient = await axiosCall(
        constants.PUT_METHOD,
        constants.GET_PATIENT + `/${patientId}`,
        patientInfo,
        null,
        user.token
      );

      if (updatePatient.isSuccess) {
        setPatientInfo({
          UHID: "",
          firstName: "",
          lastName: "",
          phone: "",
          dob: "",
          age: "",
          gender: "",
          address: "",
          city: "",
          state: "",
          pinCode: "",
          registrationDate: new Date(),

          emergencyContactName: "",
          emergencyContactRelationship: "",
          emergencyContactNumber: "",

          insuranceProvider: "",
          policyNumber: "",
          coverage: "",

          patientImage: "",
        });
        navigate("/patient");
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let newPatientInfo = { ...patientInfo, [name]: value };

    if (name === "age" && value !== "") {
      // Prevent negative age values
      const ageValue = parseInt(value);
      if (ageValue < 0) {
        // If negative, set to empty or 0
        newPatientInfo.age = "";
        setPatientInfo(newPatientInfo);
        return;
      }
      // Calculate DOB from age (using January 1st of the calculated year)
      const currentYear = new Date().getFullYear();
      const birthYear = currentYear - ageValue;
      // Explicitly set to January 1st of the birth year
      const calculatedDOB = new Date(Date.UTC(birthYear, 0, 1)); // January is 0 in JS Date
      newPatientInfo.dob = calculatedDOB.toISOString().substring(0, 10);
    } else if (name === "dob" && value) {
      // Calculate age from DOB
      const dobDate = new Date(value);
      const today = new Date();
      let age = today.getFullYear() - dobDate.getFullYear();
      // Adjust age if birthday hasn't occurred this year
      const monthDiff = today.getMonth() - dobDate.getMonth();
      const dayDiff = today.getDate() - dobDate.getDate();
      if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
        age--;
      }
      newPatientInfo.age = age.toString();
    }

    setPatientInfo(newPatientInfo);
  };

  const handleCityStateChange = (e) => {
    const { name, value } = e.target;
    if (name === "city") {
      setPatientInfo({
        ...patientInfo,
        city: value._id,
        state: value.state,
      });
    }

    if (name === "state") {
      setPatientInfo({
        ...patientInfo,
        city: "",
        state: value,
      });
    }
  };

  const fileInputRef = useRef(null);
  const fileInputEditRef = useRef(null);

  const handleUploadClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleUploadEditClick = () => {
    if (fileInputEditRef.current) {
      fileInputEditRef.current.click();
    }
  };

  return (
    <Container maxWidth={false} disableGutters>
      <Header />
      <Box sx={{ display: "flex" }}>
        <Box sx={{}}>
          <Sidebar />
        </Box>
        <Box sx={{ width: "100%" }}>
          {/* Form */}

          <Box
            component="form"
            onSubmit={handleFormSubmit}
            noValidate={false}
            sx={{
              padding: 2,
              marginTop: 2,
              borderRadius: 2,
              marginRight: "90px",
            }}
          >
            <Button
              // component={Link}
              startIcon={<ArrowLeft />}
              onClick={() => navigate(-1)}
              sx={{
                color: '#5D6A82', 
                textTransform: 'none', 
                fontSize: '16px',
                fontWeight: 'bold',
                padding: 0,
                transition: 'none !important',
                marginBottom: '16px',
              }}
            >
              Back
            </Button>
            {/* Patient Form */}
            <Typography
              sx={{
                fontFamily: 'Open Sans',
                fontWeight: 600,
                fontSize: '20px',
                lineHeight: '29px',
                letterSpacing: '0px',
                color: "#5D6A82",
                marginBottom: "1rem",
              }}
            >
              {`${isEditMode ? "Update" : "Enter"} Patient Details`}
            </Typography>

            {/* Patient Information */}
            <Box
              sx={{
                backgroundColor: "#fff",
                borderRadius: 2,
                marginBottom: 2,
                display: "flex",
                gap: 4,
              }}
            >
              <Box
                sx={{
                  width: "25rem",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "space-evenly",
                }}
              >
                <Box
                  sx={{
                    width: "100%",
                    height: "3.5rem",
                    background: "#92a6cc",
                    borderRadius: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    position: "relative",
                    padding: "0 8px",
                    boxSizing: "border-box",
                  }}
                >
                  <Typography 
                    color={"#fff"}
                    sx={{
                      fontSize: "0.95rem",
                      fontWeight: "600",
                      letterSpacing: "0.5px",
                      textAlign: "center",
                      wordBreak: "keep-all",
                      whiteSpace: "nowrap",
                    }}
                  >
                    UHID: {patientInfo.UHID ? String(patientInfo.UHID) : ""}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    width: "100%",
                    height: "15rem",
                    border: "1px solid #5d6a82",
                    borderRadius: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    position: "relative",
                    overflow: "hidden",
                    boxSizing: "border-box",
                  }}
                >
                  {/* TODO: Image */}
                  {patientInfo.patientImage && (
                    <>
                      <img
                        src={patientInfo.patientImage}
                        alt="Patient"
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                      <Button
                        variant=""
                        sx={{
                          color: "#5d6a82",
                          background: "#dfe8ef",
                          position: "absolute",
                          bottom: 8,
                          right: 8,
                        }}
                        onClick={handleUploadEditClick}
                      >
                        Edit
                      </Button>
                      <input
                        type="file"
                        ref={fileInputEditRef}
                        style={{ display: "none" }}
                        onChange={(e) => handleUpload(e)}
                        inputProps={{ accept: "image/*" }}
                      />
                    </>
                  )}
                </Box>
                {!patientInfo.patientImage && (
                  <>
                    <input
                      type="file"
                      ref={fileInputRef}
                      style={{ display: "none" }}
                      onChange={(e) => handleUpload(e)}
                      inputProps={{ accept: "image/*" }}
                    />
                    <Button
                      variant="outlined"
                      sx={{
                        borderRadius: 2,
                        width: "100%",
                        height: "3rem",
                        color: "#0179B0",
                        textTransform: "none",
                      }}
                      onClick={handleUploadClick}
                    >
                      Upload
                    </Button>
                  </>
                )}
              </Box>
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                {/* Personal Information */}

                <Box
                  sx={{
                    padding: 2,
                    backgroundColor: "#f5faff",
                    borderRadius: 2,
                    marginBottom: 2,
                  }}
                >
                  <Typography variant="subtitle1" marginBottom={2} color="grey">
                    Personal Information
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                    <Box sx={{ display: "flex", gap: 1, flex: "1 1 30%" }}>
                      <FormControl sx={{ minWidth: 80 }}>
                        <InputLabel
                          sx={{ backgroundColor: "#f5faff", padding: "0px 4px" }}
                        >
                          Title
                        </InputLabel>
                        <Select
                          value={patientInfo.title || ""}
                          onChange={handleInputChange}
                          name="title"
                          size="medium"
                        >
                          <MenuItem value="">None</MenuItem>
                          <MenuItem value="Mr.">Mr.</MenuItem>
                          <MenuItem value="Mrs.">Mrs.</MenuItem>
                          <MenuItem value="Dr.">Dr.</MenuItem>
                          <MenuItem value="Ms.">Ms.</MenuItem>
                          <MenuItem value="Prof.">Prof.</MenuItem>
                          <MenuItem value="Er.">Er.</MenuItem>
                          <MenuItem value="Sister">Sister</MenuItem>
                          <MenuItem value="Rev.">Rev.</MenuItem>
                          <MenuItem value="Capt.">Capt.</MenuItem>
                          <MenuItem value="Col.">Col.</MenuItem>
                          <MenuItem value="Maj.">Maj.</MenuItem>
                        </Select>
                      </FormControl>
                      <TextField
                        label="First Name"
                        value={patientInfo.firstName}
                        onChange={handleInputChange}
                        required
                        InputLabelProps={{
                          required: true,
                          sx: { '& .MuiFormLabel-asterisk': { color: 'red' } }
                        }}
                        sx={{ 
                          flex: 1,
                          '& .MuiOutlinedInput-root': {
                            '& fieldset': {
                              border: errors?.includes('firstName') ? '1px solid red' : '1px solid inherit',
                            },
                          },
                        }}
                        name="firstName"
                      />
                    </Box>
                    <TextField
                      label="Last Name"
                      // sx={{ flex: "1 1 30%" }}
                      required
                      InputLabelProps={{
                        required: true,
                        sx: { '& .MuiFormLabel-asterisk': { color: 'red' } }
                      }}
                      sx={{ 
                        flex: "1 1 30%",
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            border: errors?.includes('lastName') ? '1px solid red' : '1px solid inherit',
                          },
                        },
                       }}
                      value={patientInfo.lastName}
                      onChange={handleInputChange}
                      name="lastName"
                    />
                    <DatePicker
                      label="DOB"
                      value={patientInfo.dob ? dayjs(patientInfo.dob) : null}
                      onChange={(newValue) => {
                        // Convert dayjs object to ISO string format (YYYY-MM-DD) for handleInputChange
                        const dateValue = newValue ? dayjs(newValue).format("YYYY-MM-DD") : "";
                        handleInputChange({
                          target: { name: "dob", value: dateValue },
                        });
                      }}
                      maxDate={dayjs()}
                      slotProps={{ textField: { fullWidth: true, required: true, InputLabelProps: { required: true, sx: { '& .MuiFormLabel-asterisk': { color: 'red' } } }, sx: { flex: "1 1 18%" } } }}
                    />
                    <TextField
                      label="Age"
                      type="number"
                      required
                      InputLabelProps={{ 
                        shrink: true,
                        required: true,
                        sx: { '& .MuiFormLabel-asterisk': { color: 'red' } }
                      }}
                      inputProps={{ min: 0 }}
                      value={patientInfo.age}
                      sx={{ 
                        flex: "1 1 8%" ,
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            border: errors?.includes('age') ? '1px solid red' : '1px solid inherit',
                          },
                        },
                       }}
                      onChange={handleInputChange}
                      name="age"
                    />

                    <FormControl sx={{ flex: "1 1 30%" }}>
                      <InputLabel
                        required
                        sx={{ 
                          backgroundColor: "#f5faff", 
                          padding: "0px 4px",
                          '& .MuiFormLabel-asterisk': { color: 'red' }
                        }}
                      >
                        Gender
                      </InputLabel>
                      <Select
                        label="Gender"
                        value={patientInfo.gender}
                        onChange={handleInputChange}
                        sx={{
                          border: errors?.includes('gender') ? '1px solid red' : '1px solid inherit'
                        }}
                        name="gender"
                      >
                        <MenuItem value="male">Male</MenuItem>
                        <MenuItem value="female">Female</MenuItem>
                        <MenuItem value="other">Other</MenuItem>
                      </Select>
                    </FormControl>
                    <TextField
                      label="Contact Number"
                      value={patientInfo.phone}
                      onChange={handleInputChange}
                      required
                      InputLabelProps={{
                        required: true,
                        sx: { '& .MuiFormLabel-asterisk': { color: 'red' } }
                      }}
                      sx={{ 
                        flex: "1 1 30%" ,
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            border: errors?.includes('contactNo') ? '1px solid red' : '1px solid inherit',
                          },
                        },
                       }}
                      
                      inputProps={{
                        maxLength: 10,
                      }}
                      name="phone"
                    />
                    <TextField
                      label="Address"
                      value={patientInfo.address}
                      onChange={handleInputChange}
                      required
                      InputLabelProps={{
                        required: true,
                        sx: { '& .MuiFormLabel-asterisk': { color: 'red' } }
                      }}
                      name="address"
                      sx={{ 
                        flex: "1 1 30%",
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            border: errors?.includes('state') ? '1px solid red' : '1px solid inherit',
                          },
                        },
                    }}
                    />
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6} md={4}>
                        <Autocomplete
                          options={
                            patientInfo.state
                              ? cityList.filter(
                                  (s) => s.state._id === patientInfo.state
                                )
                              : cityList || []
                          }
                          fullWidth
                          getOptionLabel={(option) => option?.cityName || ""}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="City"
                              name="city"
                              required
                              InputLabelProps={{
                                required: true,
                                sx: { '& .MuiFormLabel-asterisk': { color: 'red' } }
                              }}
                              sx={{ 
                                '& .MuiOutlinedInput-root': {
                                  '& fieldset': {
                                    border: errors?.includes('city') ? '1px solid red' : '1px solid inherit',
                                  },
                                },
                               }}
                              fullWidth
                            />
                          )}
                          value={
                            cityList.find((s) => s._id === patientInfo.city) ||
                            null
                          }
                          onChange={(_, newValue) =>
                            handleCityStateChange({
                              target: {
                                name: "city",
                                value: newValue
                                  ? {
                                      _id: newValue._id,
                                      state: newValue.state?._id,
                                    }
                                  : "",
                              },
                            })
                          }
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <Autocomplete
                          options={stateList || []}
                          fullWidth
                          getOptionLabel={(option) => option?.stateName || ""}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="State"
                              name="state"
                              required
                              InputLabelProps={{
                                required: true,
                                sx: { '& .MuiFormLabel-asterisk': { color: 'red' } }
                              }}
                              sx={{ 
                                '& .MuiOutlinedInput-root': {
                                  '& fieldset': {
                                    border: errors?.includes('state') ? '1px solid red' : '1px solid inherit',
                                  },
                                },
                               }}
                              fullWidth
                            />
                          )}
                          value={
                            stateList.find(
                              (s) => s._id === patientInfo.state
                            ) || null
                          }
                          onChange={(_, newValue) =>
                            handleCityStateChange({
                              target: {
                                name: "state",
                                value: newValue ? newValue._id : "",
                              },
                            })
                          }
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <TextField
                          label="Pincode"
                          required
                          InputLabelProps={{
                            required: true,
                            sx: { '& .MuiFormLabel-asterisk': { color: 'red' } }
                          }}
                          sx={{ flex: "1 1 30%",
                            '& .MuiOutlinedInput-root': {
                              '& fieldset': {
                                border: errors?.includes('pinCode') ? '1px solid red' : '1px solid inherit',
                              },
                            },
                           }}
                          value={patientInfo.pinCode}
                          onChange={handleInputChange}
                          fullWidth
                          inputProps={{
                            maxLength: 6,
                          }}
                          name="pinCode"
                        />
                      </Grid>
                    </Grid>
                  </Box>
                </Box>

                {/* Emergency Contact */}
                <Box
                  sx={{
                    padding: 2,
                    backgroundColor: "#f5faff",
                    borderRadius: 2,
                    marginBottom: 2,
                  }}
                >
                  <Typography variant="subtitle1" color="grey" marginBottom={2}>
                    Emergency Contact
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                    <TextField
                      label="Name"
                      value={patientInfo.emergencyContactName}
                      onChange={handleInputChange}
                      required
                      InputLabelProps={{
                        required: true,
                        sx: { '& .MuiFormLabel-asterisk': { color: 'red' } }
                      }}
                      name="emergencyContactName"
                      sx={{ 
                        flex: "1 1 30%",
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            border: errors?.includes('state') ? '1px solid red' : '1px solid inherit',
                          },
                        },
                        }}
                    />
                    <Autocomplete
                      sx={{ flex: "1 1 30%" }}
                      options={relationshipList || []}
                      getOptionLabel={(option) => option.relationshipName || ""}
                      value={relationshipList?.find(relationship => relationship.relationshipName === patientInfo.emergencyContactRelationship) || null}
                      onChange={(event, newValue) => {
                        handleInputChange({
                          target: {
                            name: "emergencyContactRelationship",
                            value: newValue ? newValue.relationshipName : ""
                          }
                        });
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Relationship"
                          required
                          InputLabelProps={{
                            required: true,
                            sx: { '& .MuiFormLabel-asterisk': { color: 'red' } }
                          }}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              '& fieldset': {
                                border: errors?.includes('emergencyContactRelationship') ? '1px solid red' : '1px solid inherit',
                              },
                            },
                          }}
                        />
                      )}
                    />
                    <TextField
                      label="Contact Number"
                      value={patientInfo.emergencyContactNumber}
                      onChange={handleInputChange}
                      required
                      InputLabelProps={{
                        required: true,
                        sx: { '& .MuiFormLabel-asterisk': { color: 'red' } }
                      }}
                      name="emergencyContactNumber"
                      inputProps={{
                        maxLength: 10,
                      }}
                      sx={{ 
                        flex: "1 1 30%",
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            border: errors?.includes('state') ? '1px solid red' : '1px solid inherit',
                          },
                        },
                        }}
                    />
                  </Box>
                </Box>
              </Box>
            </Box>

            {/* Medical History */}
            {/* <Box
              sx={{
                padding: 2,
                backgroundColor: "#f5faff",
                borderRadius: 2,
                marginBottom: 2,
              }}
            >
              <Typography variant="subtitle1" color="grey" marginBottom={2}>
                Medical History
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                <FormControl sx={{ flex: "1 1 20%" }}>
                  <InputLabel
                    sx={{ backgroundColor: "#f5faff", padding: "0px 4px" }}
                  >
                    Blood Group
                  </InputLabel>
                  <Select
                    label="Blood Group"
                    value={patientInfo.bloodGroup}
                    onChange={handleInputChange}
                    name="bloodGroup"
                  >
                    <MenuItem value="A+">A+</MenuItem>
                    <MenuItem value="A-">A-</MenuItem>
                    <MenuItem value="B+">B+</MenuItem>
                    <MenuItem value="B-">B-</MenuItem>
                    <MenuItem value="AB+">AB+</MenuItem>
                    <MenuItem value="AB-">AB-</MenuItem>
                    <MenuItem value="O+">O+</MenuItem>
                    <MenuItem value="O-">O-</MenuItem>
                  </Select>
                </FormControl>
                <FormControl sx={{ flex: "1 1 20%" }}>
                  <InputLabel
                    sx={{ backgroundColor: "#f5faff", padding: "0px 4px" }}
                  >
                    Allergies
                  </InputLabel>
                  <Select
                    label="Allergies"
                    value={patientInfo.allergies}
                    onChange={handleInputChange}
                    name="allergies"
                  >
                    <MenuItem value="None">None</MenuItem>
                    <MenuItem value="Dust">Dust</MenuItem>
                    <MenuItem value="Pollen">Pollen</MenuItem>
                    <MenuItem value="Peanuts">Peanuts</MenuItem>
                    <MenuItem value="Seafood">Seafood</MenuItem>
                    <MenuItem value="Lactose">Lactose</MenuItem>
                    <MenuItem value="Gluten">Gluten</MenuItem>
                  </Select>
                </FormControl>
                <FormControl sx={{ flex: "1 1 20%" }}>
                  <InputLabel
                    sx={{ backgroundColor: "#f5faff", padding: "0px 4px" }}
                  >
                    Pregnancies
                  </InputLabel>
                  <Select
                    label="Pregnancies"
                    value={patientInfo.pregnancies}
                    onChange={handleInputChange}
                    name="pregnancies"
                  >
                    <MenuItem value="No">No</MenuItem>
                    <MenuItem value="Yes">Yes</MenuItem>
                  </Select>
                </FormControl>
                <FormControl sx={{ flex: "1 1 20%" }}>
                  <InputLabel
                    sx={{ backgroundColor: "#f5faff", padding: "0px 4px" }}
                  >
                    Differently Abled
                  </InputLabel>
                  <Select
                    label="Differently Abled"
                    value={patientInfo.differentlyAbled}
                    onChange={handleInputChange}
                    name="differentlyAbled"
                  >
                    <MenuItem value="No">No</MenuItem>
                    <MenuItem value="Yes">Yes</MenuItem>
                    <MenuItem value="Visually Impaired">
                      Visually Impaired
                    </MenuItem>
                    <MenuItem value="Hearing Impaired">
                      Hearing Impaired
                    </MenuItem>
                    <MenuItem value="Physically Challenged">
                      Physically Challenged
                    </MenuItem>
                    <MenuItem value="Other">Other</MenuItem>
                  </Select>
                </FormControl>

                <TextField
                  label="Past Surgeries"
                  placeholder="Briefly describe any surgeries the patient has undergone."
                  multiline
                  rows={3}
                  sx={{ flex: "1 1 Calc(50% - 8px)" }}
                  value={patientInfo.pastSurgeries}
                  onChange={handleInputChange}
                  name="pastSurgeries"
                />
                <TextField
                  label="Chronic Diseases"
                  placeholder="Describe any long-term illness such as diabetees, hypertension,etc."
                  multiline
                  rows={3}
                  sx={{ flex: "1 1 Calc(50% - 8px)" }}
                  value={patientInfo.chronicDiseases}
                  onChange={handleInputChange}
                  name="chronicDiseases"
                />
              </Box>
            </Box> */}

            {/* Insurance Info */}
            <Box
              sx={{
                padding: 2,
                backgroundColor: "#f5faff",
                borderRadius: 2,
                marginBottom: 2,
              }}
            >
              <Typography variant="subtitle1" color="grey" marginBottom={2}>
                Insurance Information
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                <FormControl sx={{ flex: "1 1 Calc(25% - 15px)" }}>
                  <InputLabel
                    sx={{ backgroundColor: "#f5faff", padding: "0px 4px" }}
                  >
                    Insurance Provider
                  </InputLabel>
                  <Select
                    label="Insurance Provider"
                    value={patientInfo.insuranceProvider}
                    onChange={handleInputChange}
                    name="insuranceProvider"
                  >
                    <MenuItem value="LIC Health Insurance">
                      LIC Health Insurance
                    </MenuItem>
                    <MenuItem value="ICICI Lombard">ICICI Lombard</MenuItem>
                    <MenuItem value="HDFC ERGO">HDFC ERGO</MenuItem>
                    <MenuItem value="Star Health Insurance">
                      Star Health Insurance
                    </MenuItem>
                    <MenuItem value="Religare Health Insurance">
                      Religare Health Insurance
                    </MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  label="Policy Number"
                  sx={{ flex: "1 1 Calc(25% - 15px)" }}
                  value={patientInfo.policyNumber}
                  onChange={handleInputChange}
                  name="policyNumber"
                />

                <TextField
                  label="Coverage Details (Brief Description if necessary)"
                  sx={{ flex: "1 1 Calc(50% - 8px)" }}
                  value={patientInfo.coverage}
                  onChange={handleInputChange}
                  name="coverage"
                />
              </Box>
            </Box>

            {/* Referral Info */}
            {/* <Box
              sx={{
                padding: 2,
                backgroundColor: "#f5faff",
                borderRadius: 2,
                marginBottom: 2,
              }}
            >
              <Typography variant="subtitle1" color="grey" marginBottom={2}>
                Referral (if Applicable)
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                <TextField
                  label="Referring Physician's Name"
                  sx={{ flex: "1 1 Calc(50% - 8px)" }}
                  value={patientInfo.referringPhysician}
                  onChange={handleInputChange}
                  name="referringPhysician"
                />
                <TextField
                  label="Contact Number"
                  sx={{ flex: "1 1 Calc(50% - 8px)" }}
                  value={patientInfo.referringContact}
                  onChange={handleInputChange}
                  name="referringContact"
                />
                <TextField
                  label="Reason for Referral"
                  sx={{ flex: "1 1 Calc(50% - 8px)" }}
                  value={patientInfo.referringReason}
                  onChange={handleInputChange}
                  name="referringReason"
                />

                <TextField
                  label="Date of Referral"
                  type="date"
                  sx={{ flex: "1 1 Calc(50% - 8px)" }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  value={patientInfo.dateOfReferral}
                  onChange={handleInputChange}
                  name="dateOfReferral"
                />
              </Box>
            </Box> */}

            {/* Register Button */}
            <Box sx={{ textAlign: "center" }}>
              <Button
                variant="contained"
                color="primary"
                type="submit"
                sx={{ padding: "10px 40px", width: "30%" }}
              >
                {`${isEditMode ? "Update" : "Register"}`}
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
    </Container>
  );
}

export default PatientForm;
